"use client"
import { useState, useEffect } from 'react'
import { X, Mail, ArrowRight, CheckCircle2, Sparkles, Star } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import Image from 'next/image'

export function NewsletterPopup() {
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    // Check if user has dismissed the popup before
    const hasSeenPopup = localStorage.getItem('newsletter_popup_seen')

    if (!hasSeenPopup) {
      // Show popup after a short delay
      const timer = setTimeout(() => {
        setIsOpen(true)
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [])

  const handleClose = () => {
    setIsClosing(true)
    // Wait for close animation to complete
    setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
      // Remember that user has seen the popup
      localStorage.setItem('newsletter_popup_seen', 'true')
    }, 300)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const isValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
    if (!isValid) {
      setError("Please enter a valid email address")
      setLoading(false)
      return
    }

    try {
      // Use Google Sheets API directly (Zoho OAuth has scope limitations)
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      })

      const data = await res.json()

      if (!res.ok || data?.ok === false) {
        setError(data?.message || "Something went wrong. Please try again.")
      } else {
        setSuccess(true)
        setEmail("")
        // Close popup after success message is shown
        setTimeout(() => {
          handleClose()
          // Reset success state after popup is closed
          setTimeout(() => setSuccess(false), 500)
        }, 3000)
      }
    } catch (err) {
      setError("Network error. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300 ${isClosing ? 'opacity-0' : 'opacity-100 animate-fade-in'
        }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`relative w-full max-w-lg bg-gradient-to-br from-card via-card to-card/95 border border-border/50 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100 animate-scale-in'
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-64 h-64 bg-gradient-to-br from-primary/30 to-accent/20 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-gradient-to-br from-accent/30 to-primary/20 rounded-full blur-3xl animate-float-reverse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-full blur-3xl animate-pulse-slow" />
        </div>

        {/* Floating particles */}

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-background/80 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-background transition-all duration-200 hover:scale-110 group"
          aria-label="Close newsletter popup"
        >
          <X size={18} className="group-hover:rotate-90 transition-transform duration-200" />
        </button>

        <div className="relative z-10 p-8">
          {success ? (
            <div className="flex flex-col items-center justify-center text-center py-8 animate-fade-in">
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center animate-bounce-gentle">
                  <CheckCircle2 className="text-primary w-10 h-10 animate-scale-in" />
                </div>
                <div className="absolute -top-2 -right-2">
                  <Sparkles className="w-6 h-6 text-accent animate-spin-slow" />
                </div>
              </div>
              <h3 className="text-3xl font-heading font-bold mb-3 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Welcome Aboard!
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed">
                You've successfully joined our exclusive newsletter community.
              </p>
            </div>
          ) : (
            <div className="animate-fade-in">
              {/* Logo and branding */}
              <div className="flex items-center justify-center mb-6">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
                  <Image
                    src="/vantage-crest-icon.png"
                    alt="The Vantage Crest"
                    width={64}
                    height={64}
                    className="relative w-16 h-16 group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
              </div>

              <div className="text-center mb-8">
                <h3 className="text-3xl font-heading font-bold mb-3 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-gradient-text">
                  Stay in the Loop
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Join our exclusive community and get the latest insights, events, and opportunities delivered straight to your inbox.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="relative w-full h-12 text-lg border-2 border-border/50 focus:border-accent/50 bg-background/80 backdrop-blur-sm transition-all duration-200 hover:border-accent/30"
                    disabled={loading}
                  />
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  {error && (
                    <p className="text-destructive text-sm mt-2 animate-shake">{error}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white shadow-lg hover:shadow-xl transition-all duration-200 group relative overflow-hidden"
                  disabled={loading}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {loading ? (
                    <span className="flex items-center justify-center relative z-10">
                      <span className="animate-spin mr-3 h-5 w-5 border-2 border-current border-t-transparent rounded-full"></span>
                      Subscribing...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center relative z-10">
                      Subscribe Now
                      <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-xs text-muted-foreground">
                  No spam, unsubscribe at any time. We respect your privacy.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}